import React, { useState, useEffect, useMemo } from "react";
import { useLocation, useSearchParams } from "react-router-dom";
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import Popper from '@mui/material/Popper';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Button from '@mui/material/Button';
import Tooltip from '@mui/material/Tooltip';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import IconButton from '@mui/material/IconButton';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import StopCircle from '@mui/icons-material/StopCircle';
import UndoIcon from '@mui/icons-material/UndoRounded';
import ShareIcon from '@mui/icons-material/Share';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import EmailIcon from '@mui/icons-material/Email';
import CloseIcon from '@mui/icons-material/Close';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import Snackbar from '@mui/material/Snackbar';

import DataChart from "./DataChart";
import DataTable from "./DataTable";
import DatetimeRangePicker from "../common/DatetimeRangePicker";
import Clipboard from "../common/Clipboard";
import { getMetadata, queryData, getCurlCommand, getPythonSnippet } from "../../utils/api";
import { computeAverages } from "../../utils/dataHelpers";

const quickRanges = {
  'custom': 'Custom range',
  '-1m': 'last minute',
  '-5m': 'last 5 mins',
  '-15m': 'last 15 mins',
  '-30m': 'last 30 mins',
  '-1h': 'last hour',
  '-12h': 'last 12 hours',
  '-24h': 'last day',
  '-2d': 'last 2 days',
  '-7d': 'last 7 days',
  '-30d': 'last 30 days [slow]',
  '-90d': 'last 90 days [very slow]'
};

const isDateStr = (str) => !!Date.parse(str);

const getStartTime = (start) => {
  if (isDateStr(start)) {
    return new Date(start);
  }
  
  const amount = Number(start.slice(0, -1)) || 1;
  const unit = start.charAt(start.length - 1);
  const datetime = new Date();
  
  if (unit === 'm')
    datetime.setMinutes(datetime.getMinutes() + amount);
  else if (unit === 'h')
    datetime.setHours(datetime.getHours() + amount);
  else if (unit === 'd')
    datetime.setDate(datetime.getDate() + amount);
  
  return datetime;
};

const getEndTime = (start, end) => {
  if (isDateStr(end)) {
    return new Date(end);
  }
  return new Date();
};

function getCitationText(filters, timezone) {
  const currentDate = new Date().toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric'
  });
  const currentYear = new Date().getFullYear();
  
  // Build location text
  const locations = filters.location.length > 0 
    ? filters.location.join(', ')
    : '[location]';
  
  // Build additional filters text
  const additionalInfo = [];
  if (filters.name.length > 0) {
    additionalInfo.push(`Variables: ${filters.name.join(', ')}`);  // Fixed: was using backticks instead of parentheses
  }
  if (filters.device_profile.length > 0) {
    additionalInfo.push(`Device Types: ${filters.device_profile.join(', ')}`);  // Fixed
  }
  if (filters.tree_species.length > 0) {
    additionalInfo.push(`Tree Species: ${filters.tree_species.join(', ')}`);  // Fixed
  }
  
  const additionalText = additionalInfo.length > 0 
    ? `\n(${additionalInfo.join('; ')})` 
    : '';
  
  return `CROCUS Urban Climate Research Project. ${currentYear}. 
Environmental monitoring data from ${locations}${additionalText}. 
University of Illinois Chicago. 
Funded by the U.S. Department of Energy's Office of Science. 
Accessed ${currentDate} from https://portal.crocus-urban.org. 
Timezone: ${timezone}.`;
}

export default function DataPage() {
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  
  const urlLocation = searchParams.get('location');
  const urlStart = searchParams.get('start');
  const urlEnd = searchParams.get('end');
  const urlPage = searchParams.get('page') || '0';
  const urlDeviceProfile = searchParams.get('device_profile');
  const urlDeviceId = searchParams.get('device_id');
  const urlVariable = searchParams.get('name');
  const urlSiteId = searchParams.get('site_id');
  const urlTreeSpecies = searchParams.get('tree_species');
  const urlSensorName = searchParams.get('sensor_name');
  const urlTimezone = searchParams.get('timezone');
  const urlTail = searchParams.get('tail') || '';
  
  const initial = location.state || {};
  
  // API Metadata state
  const [metadata, setMetadata] = useState(null);
  const [metadataLoading, setMetadataLoading] = useState(true);
  const [metadataError, setMetadataError] = useState(null);
  
  // Get timezone from header (localStorage)
  const [timezone, setTimezone] = useState(() => {
    return urlTimezone || localStorage.getItem('crocus_timezone') || 'UTC';
  });
  
  // Listen for timezone changes from header
  useEffect(() => {
    const handleTimezoneChange = (e) => {
      setTimezone(e.detail);
    };
    
    window.addEventListener('timezoneChange', handleTimezoneChange);
    return () => window.removeEventListener('timezoneChange', handleTimezoneChange);
  }, []);
  
  // Filter state - using API field names (now supporting multiple values)
  const [filters, setFilters] = useState({
    location: initial.locations || (urlLocation ? urlLocation.split('|') : []),
    device_profile: initial.deviceTypes || (urlDeviceProfile ? urlDeviceProfile.split('|') : []),
    device_id: initial.deviceIds || (urlDeviceId ? urlDeviceId.split('|') : []),
    name: initial.variables || (urlVariable ? urlVariable.split('|') : []),
    site_id: urlSiteId ? urlSiteId.split('|') : [],
    tree_species: urlTreeSpecies ? urlTreeSpecies.split('|') : [],
    sensor_name: urlSensorName ? urlSensorName.split('|') : [],
  });
  
  const [start, setStart] = useState(urlStart || '-30m');
  const [end, setEnd] = useState(urlEnd || '');
  const [tail, setTail] = useState(urlTail);
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [error, setError] = useState("");
  const [relativeTime, setRelativeTime] = useState(true);
  const [showMeta, setShowMeta] = useState(false);
  const [showUnit, setShowUnit] = useState(false);
  const [page, setPage] = useState(parseInt(urlPage));
  const [pageSize, setPageSize] = useState(100);
  const [pendingRange, setPendingRange] = useState(null);
  const [currentQuery, setCurrentQuery] = useState(null);
  const [queryTime, setQueryTime] = useState(null);
  const [averageInterval, setAverageInterval] = useState('raw');
  const [shareSnackbar, setShareSnackbar] = useState(false);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [shareUrl, setShareUrl] = useState('');
  
  // Fetch metadata on mount
  useEffect(() => {
    getMetadata()
      .then(data => {
        setMetadata(data);
        setMetadataLoading(false);
      })
      .catch(err => {
        setMetadataError(err.message);
        setMetadataLoading(false);
      });
  }, []);
  
  // Convert metadata arrays to option format
  const locationOptions = useMemo(() => 
    metadata?.locations?.map(loc => ({ id: loc, label: loc })) || []
  , [metadata]);
  
  const deviceProfileOptions = useMemo(() => 
    metadata?.device_profiles?.map(dp => ({ id: dp, label: dp })) || []
  , [metadata]);
  
  const deviceIdOptions = useMemo(() => 
    metadata?.device_ids?.map(id => ({ id: id, label: id })) || []
  , [metadata]);
  
  const variableOptions = useMemo(() => 
    metadata?.variables?.map(v => ({ id: v, label: v })) || []
  , [metadata]);
  
  const siteIdOptions = useMemo(() => 
    metadata?.site_ids?.map(s => ({ id: s, label: s })) || []
  , [metadata]);
  
  const treeSpeciesOptions = useMemo(() => 
    metadata?.tree_species?.map(ts => ({ id: ts, label: ts })) || []
  , [metadata]);
  
  const sensorNameOptions = useMemo(() => 
    metadata?.sensor_names?.map(sn => ({ id: sn, label: sn })) || []
  , [metadata]);
  
  // Compute date range based on start/end
  const range = useMemo(() => 
    [getStartTime(start), getEndTime(start, end)],
    [start, end]
  );
  
  // Run query on initial load if filters are set
  useEffect(() => {
    if ((initial.locations || urlLocation) && metadata) {
      runQuery(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [metadata]); // Only run when metadata is loaded
  
  const updateURL = (newPage = page) => {
    const params = new URLSearchParams();
    if (filters.location.length) params.set('location', filters.location.join('|'));
    if (filters.device_profile.length) params.set('device_profile', filters.device_profile.join('|'));
    if (filters.device_id.length) params.set('device_id', filters.device_id.join('|'));
    if (filters.name.length) params.set('name', filters.name.join('|'));
    if (filters.site_id.length) params.set('site_id', filters.site_id.join('|'));
    if (filters.tree_species.length) params.set('tree_species', filters.tree_species.join('|'));
    if (filters.sensor_name.length) params.set('sensor_name', filters.sensor_name.join('|'));
    params.set('start', start);
    if (end) params.set('end', end);
    params.set('timezone', timezone);
    if (tail) params.set('tail', tail);
    params.set('page', newPage.toString());
    setSearchParams(params, { replace: true });
  };
  
  function runQuery(skipSetPage) {
    const startTime = pendingRange ? pendingRange[0].toISOString() : start;
    const endTime = pendingRange ? pendingRange[1].toISOString() : (end || undefined);
    
    // Build API query
    const apiQuery = {
      start: startTime,
      ...(endTime ? { end: endTime } : {}),
      timezone: timezone,
      filter: {}
    };
    
    // Add tail if specified
    if (tail && parseInt(tail) > 0) {
      apiQuery.tail = parseInt(tail);
    }
    
    // Add filters - support multiple values
    Object.entries(filters).forEach(([key, values]) => {
      if (values.length === 1) {
        apiQuery.filter[key] = values[0];
      } else if (values.length > 1) {
        apiQuery.filter[key] = values.join('|');
      }
    });
    
    // If no filters set, don't query
    if (Object.keys(apiQuery.filter).length === 0) {
      setError("Please select at least one filter");
      return;
    }
    
    setCurrentQuery(apiQuery);
    setLoading(true);
    setError("");
    setPendingRange(null);
    setQueryTime(null);
    
    if (!skipSetPage) {
      setPage(0);
      updateURL(0);
    } else {
      updateURL();
    }
    
    const queryStartTime = performance.now();
    
    queryData(apiQuery)
      .then(data => {
        const queryEndTime = performance.now();
        const duration = Math.round(queryEndTime - queryStartTime);
        setQueryTime(duration);
        
        // Transform API data to match component expectations
        const transformedData = data.map(record => ({
          ts: record.timestamp,
          node: record.meta.location,
          name: record.name,
          value: record.value,
          device: record.meta.DeviceName || record.meta.DeviceProfileName,
          sensorId: record.meta.DeviceID,
          devEui: record.meta.DevEUI || '',
          species: record.meta.TreeSpecies || '',
          dbh: record.meta.TreeDiameter || '',
          site: record.meta.SiteID || '',
          unit: record.meta.Unit || '',
          meta: record.meta
        }));
        
        // Sort by timestamp descending
        transformedData.sort((a, b) => new Date(b.ts) - new Date(a.ts));
        
        setRows(transformedData);
      })
      .catch(e => {
        setError(String(e?.message || e));
        setQueryTime(null);
      })
      .finally(() => {
        setLoading(false);
      });
  }
  
  const handleFilterChange = (field, val) => {
    // Handle multi-select: val is array of options or null
    const values = val ? (Array.isArray(val) ? val.map(v => typeof v === 'string' ? v : v.id) : []) : [];
    setFilters(prev => ({ ...prev, [field]: values }));
  };
  
  const handleQuickRangeChange = (value) => {
    setPendingRange(null);
    setEnd('');
    setStart(value);
    setParams(value, '');
  };
  
  const setParams = (startVal, endVal) => {
    const params = new URLSearchParams(searchParams);
    params.set('start', startVal);
    if (endVal) {
      params.set('end', endVal);
    } else {
      params.delete('end');
    }
    setSearchParams(params, { replace: true });
  };
  
  const handleDatePickerChange = ([startDate, endDate]) => {
    setPendingRange([startDate, endDate]);
  };
  
  const handleRefresh = () => {
    if (loading) {
      setLoading(false);
    } else if (pendingRange) {
      setStart(pendingRange[0].toISOString());
      setEnd(pendingRange[1].toISOString());
      setParams(pendingRange[0].toISOString(), pendingRange[1].toISOString());
      runQuery();
    } else {
      runQuery();
    }
  };
  
  const handleRemoveFilters = () => {
    // Clear all filters
    setFilters({
      location: [],
      device_profile: [],
      device_id: [],
      name: [],
      site_id: [],
      tree_species: [],
      sensor_name: []
    });
    
    // Clear URL params
    const params = new URLSearchParams();
    params.set('start', '-30m');
    params.set('timezone', timezone);
    params.set('page', '0');
    setSearchParams(params, { replace: true });
    
    // Reset state
    setStart('-30m');
    setEnd('');
    setTail('');
    setPage(0);
    setRows([]);
    setError('');
  };
  
  const total = rows.length;
  
  // Compute averaged data when interval is not 'raw'
  const displayData = useMemo(() => {
    if (averageInterval === 'raw') return rows;
    return computeAverages(rows, averageInterval);
  }, [rows, averageInterval]);
  
  const displayTotal = displayData.length;
  
  if (metadataLoading) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-4">
        <div className="flex items-center justify-center p-12">
          <CircularProgress />
          <span className="ml-3">Loading metadata...</span>
        </div>
      </div>
    );
  }
  
  if (metadataError) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-4">
        <Alert severity="error">
          Failed to load metadata: {metadataError}
        </Alert>
      </div>
    );
  }
  
  // Calculate total number of active filters
  const totalFilters = Object.values(filters).reduce((acc, arr) => acc + arr.length, 0);
  
  return (
    <div className="max-w-7xl mx-auto px-4 py-4">
      <div className="flex items-start justify-between mb-3">
        <div className="text-sm">
          <h2 className="text-xl font-semibold mb-2">
            Query Browser
            {queryTime !== null && (
              <small className="text-gray-500 ml-2">
                ({queryTime}ms)
              </small>
            )}
            <Tooltip title="Share query">
              <IconButton
                size="small"
                onClick={() => {
                  updateURL();
                  setTimeout(() => {
                    setShareUrl(window.location.href);
                    setShareDialogOpen(true);
                  }, 50);
                }}
                sx={{ ml: 1 }}
              >
                <ShareIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </h2>
        </div>
        <div className="flex items-center gap-3">
          <FormControl variant="outlined" size="small" style={{ minWidth: 130 }}>
            <InputLabel id="avg-label" style={{ fontSize: '14px' }}>Averaging</InputLabel>
            <Select
              labelId="avg-label"
              id="averaging"
              value={averageInterval}
              onChange={(evt) => {
                setAverageInterval(evt.target.value);
                setPage(0);
              }}
              label="Averaging"
              style={{ height: '32px', fontSize: '14px' }}
            >
              <MenuItem value="raw" style={{ fontSize: '14px' }}>Raw data</MenuItem>
              <MenuItem value="15m" style={{ fontSize: '14px' }}>15 min avg</MenuItem>
              <MenuItem value="30m" style={{ fontSize: '14px' }}>30 min avg</MenuItem>
              <MenuItem value="1h" style={{ fontSize: '14px' }}>1 hour avg</MenuItem>
              <MenuItem value="3h" style={{ fontSize: '14px' }}>3 hour avg</MenuItem>
              <MenuItem value="6h" style={{ fontSize: '14px' }}>6 hour avg</MenuItem>
              <MenuItem value="1d" style={{ fontSize: '14px' }}>1 day avg</MenuItem>
            </Select>
          </FormControl>
          
          <FormControl variant="outlined" size="small" style={{ minWidth: 150 }}>
            <InputLabel id="range-label" style={{ fontSize: '14px' }}>Quick Ranges</InputLabel>
            <Select
              labelId="range-label"
              id="range"
              value={isDateStr(start) ? 'custom' : start}
              onChange={(evt) => handleQuickRangeChange(evt.target.value)}
              label="Quick Ranges"
              style={{ height: '32px', fontSize: '14px' }}
            >
              {(isDateStr(start) ? Object.keys(quickRanges) : Object.keys(quickRanges).slice(1))
                .map(k =>
                  <MenuItem value={k} key={k} style={{ fontSize: '14px' }}>{quickRanges[k]}</MenuItem>
                )
              }
            </Select>
          </FormControl>
          
          <DatetimeRangePicker
            value={pendingRange || range}
            onChange={handleDatePickerChange}
          />
          
          <Tooltip title={loading ? 'Cancel' : (pendingRange ? 'Submit' : 'Refresh')}>
            <Button
              variant={pendingRange ? 'contained' : 'outlined'}
              color={pendingRange ? 'success' : 'info'}
              onClick={handleRefresh}
              style={{ minWidth: '40px', height: '32px' }}
            >
              {loading ? (
                <>
                  <CircularProgress size={20} />
                  <StopCircle color="action" />
                </>
              ) : (
                pendingRange ? 'Go' : <RefreshRoundedIcon color="action" />
              )}
            </Button>
          </Tooltip>
        </div>
      </div>
      
      <div className="flex gap-6">
        <aside className="w-[280px] shrink-0">
          <div className="border rounded p-3">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold">Device Filters</h3>
              {totalFilters > 0 && (
                <Tooltip title="Clear all filters">
                  <IconButton 
                    size="small" 
                    onClick={handleRemoveFilters}
                    sx={{ padding: '4px' }}
                  >
                    <UndoIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </div>
            
            <div className="mb-3 bg-white">
              <Autocomplete
                multiple
                options={locationOptions}
                renderInput={(props) => <TextField {...props} label="Location" size="small" />}
                PopperComponent={(props) =>
                  <Popper {...props} style={{ width: 300, zIndex: 9999 }} />
                }
                value={filters.location.map(id => locationOptions.find(o => o.id === id)).filter(Boolean)}
                onChange={(evt, val) => handleFilterChange('location', val)}
                isOptionEqualToValue={(opt, val) => opt.id === val?.id}
                disableCloseOnSelect
                limitTags={2}
              />
            </div>
            
            <div className="mb-3 bg-white">
              <Autocomplete
                multiple
                options={variableOptions}
                renderInput={(props) => <TextField {...props} label="Variable" size="small" />}
                PopperComponent={(props) =>
                  <Popper {...props} style={{ width: 300, zIndex: 9999 }} />
                }
                value={filters.name.map(id => variableOptions.find(o => o.id === id)).filter(Boolean)}
                onChange={(evt, val) => handleFilterChange('name', val)}
                isOptionEqualToValue={(opt, val) => opt.id === val?.id}
                disableCloseOnSelect
                limitTags={2}
              />
            </div>
            
            <div className="mb-3 bg-white">
              <Autocomplete
                multiple
                options={deviceProfileOptions}
                renderInput={(props) => <TextField {...props} label="Device Profile" size="small" />}
                PopperComponent={(props) =>
                  <Popper {...props} style={{ width: 300, zIndex: 9999 }} />
                }
                value={filters.device_profile.map(id => deviceProfileOptions.find(o => o.id === id)).filter(Boolean)}
                onChange={(evt, val) => handleFilterChange('device_profile', val)}
                isOptionEqualToValue={(opt, val) => opt.id === val?.id}
                disableCloseOnSelect
                limitTags={2}
              />
            </div>
            
            <div className="mb-3 bg-white">
              <Autocomplete
                multiple
                options={deviceIdOptions}
                renderInput={(props) => <TextField {...props} label="Device ID" size="small" />}
                PopperComponent={(props) =>
                  <Popper {...props} style={{ width: 300, zIndex: 9999 }} />
                }
                value={filters.device_id.map(id => deviceIdOptions.find(o => o.id === id)).filter(Boolean)}
                onChange={(evt, val) => handleFilterChange('device_id', val)}
                isOptionEqualToValue={(opt, val) => opt.id === val?.id}
                disableCloseOnSelect
                limitTags={2}
              />
            </div>
            
            <div className="mb-3 bg-white">
              <Autocomplete
                multiple
                options={siteIdOptions}
                renderInput={(props) => <TextField {...props} label="Site ID" size="small" />}
                PopperComponent={(props) =>
                  <Popper {...props} style={{ width: 300, zIndex: 9999 }} />
                }
                value={filters.site_id.map(id => siteIdOptions.find(o => o.id === id)).filter(Boolean)}
                onChange={(evt, val) => handleFilterChange('site_id', val)}
                isOptionEqualToValue={(opt, val) => opt.id === val?.id}
                disableCloseOnSelect
                limitTags={2}
              />
            </div>
            
            <div className="mb-3 bg-white">
              <Autocomplete
                multiple
                options={treeSpeciesOptions}
                renderInput={(props) => <TextField {...props} label="Tree Species" size="small" />}
                PopperComponent={(props) =>
                  <Popper {...props} style={{ width: 300, zIndex: 9999 }} />
                }
                value={filters.tree_species.map(id => treeSpeciesOptions.find(o => o.id === id)).filter(Boolean)}
                onChange={(evt, val) => handleFilterChange('tree_species', val)}
                isOptionEqualToValue={(opt, val) => opt.id === val?.id}
                disableCloseOnSelect
                limitTags={2}
              />
            </div>
            
            <div className="mb-3 bg-white">
              <Autocomplete
                multiple
                options={sensorNameOptions}
                renderInput={(props) => <TextField {...props} label="Sensor Name" size="small" />}
                PopperComponent={(props) =>
                  <Popper {...props} style={{ width: 300, zIndex: 9999 }} />
                }
                value={filters.sensor_name.map(id => sensorNameOptions.find(o => o.id === id)).filter(Boolean)}
                onChange={(evt, val) => handleFilterChange('sensor_name', val)}
                isOptionEqualToValue={(opt, val) => opt.id === val?.id}
                disableCloseOnSelect
                limitTags={2}
              />
            </div>
            
            <div className="mb-3">
              <TextField
                label="Tail Limit"
                type="number"
                size="small"
                fullWidth
                value={tail}
                onChange={(e) => setTail(e.target.value)}
                placeholder="No limit"
                helperText="Limit to most recent N records"
              />
            </div>
            
            <button 
              onClick={() => runQuery()} 
              className="mt-4 w-full bg-blue-600 text-white rounded py-2 text-sm hover:bg-blue-700"
              disabled={loading}
            >
              {loading ? "Querying…" : "Run query"}
            </button>
            
            {error && <div className="text-red-600 text-xs mt-2">{error}</div>}
            
            {/* Code Snippets */}
            {currentQuery && (
              <div className="mt-6">
                <div className="flex justify-between text-xs mb-3">
                  <a 
                    href="https://data.crocus-micronet.org" 
                    target="_blank" 
                    rel="noreferrer"
                    className="text-blue-600 hover:underline"
                  >
                    API link
                  </a>
                  <a 
                    href="https://data.crocus-micronet.org/docs" 
                    target="_blank" 
                    rel="noreferrer"
                    className="text-blue-600 hover:underline"
                  >
                    docs
                  </a>
                </div>
                
                <h4 className="font-semibold mb-2 text-sm">cURL download</h4>
                <Clipboard
                  content={getCurlCommand(currentQuery)}
                  tooltip="Copy curl command"
                />
                
                <h4 className="font-semibold mb-2 text-sm mt-4">Python snippet</h4>
                <Clipboard
                  content={getPythonSnippet(currentQuery)}
                  tooltip="Copy Python snippet"
                />
                
                <h4 className="font-semibold mb-2 text-sm mt-4">Citation</h4>
                <Clipboard
                  content={getCitationText(filters, timezone)}
                  tooltip="Copy citation"
                />
              </div>
            )}
          </div>
        </aside>
        
        <main className="flex-1">
          <div className="border rounded p-3">
            <DataChart rows={displayData} />
            
            <DataTable
              rows={displayData}
              total={displayTotal}
              page={page}
              setPage={(newPage) => {
                setPage(newPage);
                updateURL(newPage);
              }}
              pageSize={pageSize}
              setPageSize={setPageSize}
              relativeTime={relativeTime}
              setRelativeTime={setRelativeTime}
              showMeta={showMeta}
              setShowMeta={setShowMeta}
              showUnit={showUnit}
              setShowUnit={setShowUnit}
              loading={loading}
              averageInterval={averageInterval}
              rawTotal={total}
            />
          </div>
        </main>
      </div>
      {/* Share Dialog */}
      <Dialog
        open={shareDialogOpen}
        onClose={() => setShareDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 0 }}>
          <span style={{ fontSize: '1rem', fontWeight: 600 }}>Share Query</span>
          <IconButton size="small" onClick={() => setShareDialogOpen(false)}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          {/* URL display + copy */}
          <div className="flex items-center gap-2 mb-4">
            <input
              readOnly
              value={shareUrl}
              className="flex-1 text-xs font-mono bg-gray-50 border rounded px-3 py-2 text-gray-700 truncate"
              onClick={(e) => e.target.select()}
            />
            <Tooltip title="Copy link">
              <IconButton
                size="small"
                onClick={() => {
                  navigator.clipboard.writeText(shareUrl);
                  setShareSnackbar(true);
                }}
                sx={{ border: '1px solid #e5e7eb', borderRadius: 1 }}
              >
                <ContentCopyIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </div>

          {/* Share options */}
          <p className="text-xs text-gray-400 uppercase tracking-wide font-medium mb-2">Share via</p>
          <div className="flex flex-wrap gap-2">
            <Button
              variant="outlined"
              size="small"
              startIcon={<EmailIcon />}
              onClick={() => {
                window.open(
                  `mailto:?subject=${encodeURIComponent('CROCUS Portal — Shared Query')}&body=${encodeURIComponent(`Check out this query:\n\n${shareUrl}`)}`,
                  '_self'
                );
              }}
              sx={{ textTransform: 'none', fontSize: '0.8rem', borderColor: '#d1d5db', color: '#374151' }}
            >
              Email
            </Button>

            <Button
              variant="outlined"
              size="small"
              startIcon={<OpenInNewIcon />}
              onClick={() => {
                window.open(shareUrl, '_blank');
              }}
              sx={{ textTransform: 'none', fontSize: '0.8rem', borderColor: '#d1d5db', color: '#374151' }}
            >
              Open in new tab
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Snackbar
        open={shareSnackbar}
        autoHideDuration={2000}
        onClose={() => setShareSnackbar(false)}
        message="Link copied to clipboard"
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </div>
  );
}